import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import BookingDeadlineDialog from '~/client/src/shared/components/BookingDeadlineDialog/BookingDeadlineDialog'
import NonWorkTimeConfirmDialog from '~/client/src/shared/components/NonWorkTimeConfirmDialog/NonWorkTimeConfirmDialog'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '~/client/src/shared/components/shadcn-ui/drawer'
import {
  Popover,
  PopoverContent,
} from '~/client/src/shared/components/shadcn-ui/popover'
import ClosuresConflictDialog from '~/client/src/shared/components/SitePermitCreationForm/ClosuresConflictDialog/ClosuresConflictDialog'
import PermitContentPickers from '~/client/src/shared/components/SitePermitCreationForm/components/PermitContentPickers/PermitContentPickers'
import ResourceRestrictionsSync from '~/client/src/shared/components/SitePermitCreationForm/components/ResourceRestrictionsSync'
import HoursOfOperationDialog from '~/client/src/shared/components/SitePermitCreationForm/HoursOfOperationDialog/HoursOfOperationDialog'
import SitePermitCreationFormStore from '~/client/src/shared/components/SitePermitCreationForm/WorkflowCreationForm.store'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { getFullscreenPortalTarget } from '~/client/src/shared/utils/fullscreenElement'

// localization: no text to translate

interface IProps {
  store: SitePermitCreationFormStore
  // Desktop: rect of the field row that opened the picker; the picker anchors to it.
  anchorRect?: DOMRect | null
  // Mobile: pickers open as a bottom-sheet Drawer instead of the popover.
  isMobileEnv?: boolean
  allowedTypeIds?: string[]
}

const formEntityName = 'Form'

// Restyle the legacy side-panel pickers to work as bare bottom-sheets: drop
// inset/dimmers, unclip the scroll, pin the date-picker's Select button.
const LEGACY_PICKER_ADAPTER = [
  // `!` throughout: the sheets' unlayered scss beats layered utilities.
  // Flush at top-0 the sheet's slide-up chrome (radius, border-t) would
  // double the host's own rounded edge.
  'tw:[&_.form-dropdown-options-modal.is-shown_.compact-popup-modal.shown]:top-0!',
  'tw:[&_.form-dropdown-options-modal.is-shown_.compact-popup-modal.shown]:h-full!',
  'tw:[&_.form-dropdown-options-modal.is-shown_.compact-popup-modal.shown]:rounded-none!',
  'tw:[&_.form-dropdown-options-modal.is-shown_.compact-popup-modal.shown]:border-t-0!',
  'tw:[&_.permit-date-picker-container>.permit-date-picker]:top-0',
  'tw:[&_.permit-date-picker-container>.permit-date-picker]:h-full',
  'tw:[&_.form-dropdown-options-modal_.dimmer]:hidden',
  'tw:[&_.permit-date-picker-container>.dimmer]:hidden',
  'tw:[&_.compact-popup-modal]:min-h-0',
  'tw:[&_.compact-popup-modal_.col]:min-h-0',
  'tw:[&_.compact-popup-modal_.full-height]:min-h-0',
  'tw:[&_.compact-popup-modal_.overflow-auto]:min-h-0',
  'tw:[&_.compact-popup-modal_.overflow-y-auto]:min-h-0',
  'tw:[&_.permit-date-picker>.px16.mt5:last-child]:sticky',
  'tw:[&_.permit-date-picker>.px16.mt5:last-child]:bottom-0',
  'tw:[&_.permit-date-picker>.px16.mt5:last-child]:bg-popover',
  'tw:[&_.permit-date-picker>.px16.mt5:last-child]:py-2',
  // mt5's transparent gap and the sheet's 30px safe-area padding would let
  // scrolled content show around the pinned footer.
  'tw:[&_.permit-date-picker>.px16.mt5:last-child]:border-t',
  'tw:[&_.permit-date-picker>.px16.mt5:last-child]:border-border',
  'tw:[&_.permit-date-picker>.px16.mt5:last-child]:mt-0!',
  'tw:[&_.permit-date-picker-container>.permit-date-picker]:pb-0!',
].join(' ')

// relative: the positioner's isolate contains the legacy pickers' own z (compact
// popup, date picker, dimmer).
const DESKTOP_PICKER_CLASS = `tw:relative tw:block tw:h-[420px] tw:max-h-(--available-height) tw:w-[340px] tw:max-w-(--available-width) tw:overflow-hidden tw:p-0 ${LEGACY_PICKER_ADAPTER}`

// The date picker is content-height (absolute, max-height only) — let it flow in
// the drawer so the sheet's auto height hugs it instead of leaving blank space.
const DATE_PICKER_DRAWER_ADAPTER = [
  'tw:[&_.permit-date-picker-container]:relative!',
  'tw:[&_.permit-date-picker-container>.permit-date-picker]:relative!',
  'tw:[&_.permit-date-picker-container>.permit-date-picker]:h-auto!',
  'tw:[&_.permit-date-picker]:max-h-none!',
].join(' ')

// Host ResourceRestrictionsSync here (form-scoped): the mobile drawer unmounts
// pickers on close, and their cleanup would wipe the store's resource maps mid-session.
const ComposerSatellites: React.FC<IProps> = observer(
  ({ store, anchorRect, isMobileEnv, allowedTypeIds }) => {
    const {
      isNonWorkTimesModalOpened,
      isDeadlineModalShown,
      isClosuresConflictModalShown,
      shouldBlockLateRequesting,
      bookingDeadlineInterval,
      willBlockSubmitOnNonWorkTimeApply,
      nonWorkTimeAndDeadlineTitle,
      nonWorkTimeAndDeadlineMessage,
      setRestrictionsAndBlockSubmitIfNeed,
      moveDatesOrSetAsLate,
      closeClosuresConflictModal,
    } = store

    const isDatePicker = store.isDatePickerDisplayed
    const pickerContentRef = React.useRef<HTMLDivElement>(null)
    const { state } = React.useContext(MobXProviderContext)
    const projectId: string | undefined = state?.activeProject?.id

    // showFormTypeSelector never sets selectedField, so gating on it alone hides the selector.
    const isPickerActive = !!store.selectedField || store.isTypeSelectorShown

    const onHideNonWorkTimeDialog = () => {
      if (store.areNonWorkAndDeadlineModalsShown) {
        store.closeDeadlineModal()
      }
      store.closeNonWorkTimeModal()
    }

    // Observable read (this component is an observer) — reacts to fullscreen changes.
    const portalTarget = getFullscreenPortalTarget()
    const virtualAnchor = React.useMemo(
      () =>
        anchorRect ? { getBoundingClientRect: () => anchorRect } : undefined,
      [anchorRect],
    )

    const pickerTitle = store.isTypeSelectorShown
      ? Localization.translator.formType
      : store.selectedField?.caption ||
        store.selectedField?.type ||
        Localization.translator.select

    const gateDialogs = (
      <>
        <NonWorkTimeConfirmDialog
          isShown={isNonWorkTimesModalOpened}
          onHide={onHideNonWorkTimeDialog}
          onApply={setRestrictionsAndBlockSubmitIfNeed}
          shouldBlockSubmit={willBlockSubmitOnNonWorkTimeApply}
          entityName={formEntityName}
          title={nonWorkTimeAndDeadlineTitle}
          message={nonWorkTimeAndDeadlineMessage}
          portalContainer={portalTarget}
        />
        <BookingDeadlineDialog
          isShown={!isNonWorkTimesModalOpened && isDeadlineModalShown}
          shouldBlockLateRequesting={shouldBlockLateRequesting}
          deadlineInterval={bookingDeadlineInterval}
          onHide={store.closeDeadlineModal}
          onApply={moveDatesOrSetAsLate}
          portalContainer={portalTarget}
        />
        <ClosuresConflictDialog
          isShown={isClosuresConflictModalShown}
          isRecurring={false}
          onContinue={store.continueDespiteClosuresConflict}
          onCancel={closeClosuresConflictModal}
          onApply={closeClosuresConflictModal}
          portalContainer={portalTarget}
        />
        <HoursOfOperationDialog store={store} portalContainer={portalTarget} />
      </>
    )

    return (
      <>
        {/* Outside the pickers: they remount on every open, and its cleanup
          clears the resource maps mid-creation. */}
        <ResourceRestrictionsSync store={store} projectId={projectId} />
        {gateDialogs}
        {isMobileEnv ? (
          <Drawer
            open={isPickerActive}
            onOpenChange={open => {
              if (!open) store.closePicker()
            }}
            // Avoid the AutoSizer collapse the composer sheet also guards against
            // (STX1-443), and vaul's scroll-into-view jump on the keyboard.
            noBodyStyles
            repositionInputs={false}
          >
            <DrawerContent
              // The date picker is content-height, so the sheet hugs it; other pickers fill 85dvh.
              className={`${
                isDatePicker ? 'tw:h-auto tw:max-h-[85dvh]' : 'tw:h-[85dvh]'
              } ${LEGACY_PICKER_ADAPTER} ${
                isDatePicker ? DATE_PICKER_DRAWER_ADAPTER : ''
              }`}
              ref={pickerContentRef}
              // Don't autofocus an inner input (iOS keyboard), but do move focus here:
              // the modal aria-hides the composer, and focus left on its trigger trips aria-hidden.
              onOpenAutoFocus={e => {
                e.preventDefault()
                pickerContentRef.current?.focus()
              }}
            >
              <DrawerTitle className="tw:sr-only">{pickerTitle}</DrawerTitle>
              <DrawerDescription className="tw:sr-only">
                {pickerTitle}
              </DrawerDescription>
              {/* relative: contains the legacy picker's position:absolute sheet so it
                doesn't escape to the fixed DrawerContent and cover the rounded top. */}
              <div
                className={`tw:relative tw:flex tw:min-h-0 tw:flex-1 tw:flex-col ${
                  isDatePicker ? 'tw:overflow-y-auto' : 'tw:overflow-hidden'
                }`}
              >
                <PermitContentPickers
                  store={store}
                  isPreviewMode={false}
                  allowedTypeIds={allowedTypeIds}
                  skipResourceSync
                />
              </div>
            </DrawerContent>
          </Drawer>
        ) : (
          <Popover
            open={isPickerActive}
            modal
            onOpenChange={(open, { reason }) => {
              // Focus moving into a legacy picker's own portal is not a dismissal.
              if (!open && reason !== 'focus-out') {
                store.closePicker()
              }
            }}
          >
            <PopoverContent
              // A z-less backdrop would sit under the map's own layers in fullscreen.
              backdrop
              anchor={virtualAnchor}
              portalContainer={portalTarget}
              finalFocus={false}
              aria-label={pickerTitle}
              side="bottom"
              align="start"
              sideOffset={6}
              className={DESKTOP_PICKER_CLASS}
            >
              <PermitContentPickers
                store={store}
                isPreviewMode={false}
                allowedTypeIds={allowedTypeIds}
                skipResourceSync
              />
            </PopoverContent>
          </Popover>
        )}
      </>
    )
  },
)

export default ComposerSatellites
